import React, { useEffect, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { errorMessage } from '../errors';
import { qualificationApi, OVERALL_STATUS_LABELS, statusColor } from '../qualificationApi';
import { TableScroller } from '@/components/ui/table-scroller';
import LongTextCell from '@/components/LongTextCell';

const fmt$ = (n) => (n || n === 0) ? Number(n).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }) : '—';

function bestProgram(scenario) {
  const candidates = (scenario.result?.programs || []).filter((p) => p.status !== 'not_qualified');
  if (candidates.length === 0) return null;
  // Ticket 259: a null DTI (no income) can't be the lowest; fall back to the first candidate.
  const withDti = candidates.filter((p) => p.dti != null);
  if (withDti.length === 0) return candidates[0];
  return withDti.reduce((best, p) => (p.dti < best.dti ? p : best), withDti[0]);
}

function maxOver(scenario) {
  const candidates = (scenario.result?.programs || []).filter((p) => p.status !== 'not_qualified');
  if (candidates.length === 0) return null;
  return Math.max(...candidates.map((p) => p.max_purchase_price));
}

export default function ScenarioCompare({ ids, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    qualificationApi.compare(ids)
      .then((d) => { if (alive) setData(d); })
      .catch((e) => { if (alive) toast({ title: 'Could not compare scenarios', description: errorMessage(e) }); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [ids]);

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-8 flex items-center justify-center text-slate-400" data-testid="scenario-compare-loading">
        <Loader2 className="animate-spin mr-2" size={18} /> Comparing scenarios...
      </div>
    );
  }
  if (!data) return null;

  const { scenarios, best } = data;
  const bestPrograms = scenarios.map(bestProgram);
  const maxPurchases = scenarios.map(maxOver);

  const cellClass = (scenarioId, kind) => best?.[kind] === scenarioId ? 'bg-green-50 font-semibold' : '';
  const testidFor = (scenarioId, kind) => best?.[kind] === scenarioId ? `compare-best-${kind}` : undefined;

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4" data-testid="scenario-compare">
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm font-semibold" style={{ color: '#070B5F' }}>Compare Scenarios</div>
        <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="scenario-compare-close">
          <X size={16} />
        </button>
      </div>
      <TableScroller>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
              <th className="px-3 py-2">Metric</th>
              {scenarios.map((s) => (
                <th key={s.scenario_id} className="px-3 py-2 min-w-[8rem]">
                  <LongTextCell id={`scenario-head-${s.scenario_id}`} text={s.name} lines={2} maxWidth={180} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <Row label="Purchase price">
              {scenarios.map((s) => <td key={s.scenario_id} className="px-3 py-2">{fmt$(s.input?.purchase_price)}</td>)}
            </Row>
            <Row label="Annual income">
              {scenarios.map((s) => (
                <td key={s.scenario_id} className="px-3 py-2">
                  {s.input?.income_source === 'calculated' ? 'calculated' : fmt$(s.input?.annual_income)}
                </td>
              ))}
            </Row>
            <Row label="Credit">
              {scenarios.map((s) => (
                <td key={s.scenario_id} className="px-3 py-2">{s.input?.credit_score ?? s.input?.credit_score_range ?? '—'}</td>
              ))}
            </Row>
            <Row label="Down %">
              {scenarios.map((s) => {
                const price = s.input?.purchase_price || 0;
                const pct = price ? ((s.input?.down_payment_amount || 0) / price * 100).toFixed(1) : '—';
                return <td key={s.scenario_id} className="px-3 py-2">{pct === '—' ? '—' : `${pct}%`}</td>;
              })}
            </Row>
            <Row label="Best program">
              {scenarios.map((s, i) => <td key={s.scenario_id} className="px-3 py-2">{bestPrograms[i]?.name || '—'}</td>)}
            </Row>
            <Row label="DTI">
              {scenarios.map((s, i) => (
                <td key={s.scenario_id} className={`px-3 py-2 ${cellClass(s.scenario_id, 'dti')}`} data-testid={testidFor(s.scenario_id, 'dti')}>
                  {bestPrograms[i]?.dti != null ? `${bestPrograms[i].dti.toFixed(1)}%` : '—'}
                </td>
              ))}
            </Row>
            <Row label="Payment">
              {scenarios.map((s, i) => (
                <td key={s.scenario_id} className={`px-3 py-2 ${cellClass(s.scenario_id, 'payment')}`} data-testid={testidFor(s.scenario_id, 'payment')}>
                  {bestPrograms[i] ? fmt$(bestPrograms[i].total_payment) : '—'}
                </td>
              ))}
            </Row>
            <Row label="Max purchase">
              {scenarios.map((s, i) => (
                <td key={s.scenario_id} className={`px-3 py-2 ${cellClass(s.scenario_id, 'max_purchase')}`} data-testid={testidFor(s.scenario_id, 'max_purchase')}>
                  {maxPurchases[i] != null ? fmt$(maxPurchases[i]) : '—'}
                </td>
              ))}
            </Row>
            <Row label="Status">
              {scenarios.map((s) => (
                <td key={s.scenario_id} className="px-3 py-2">
                  <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${statusColor(s.result?.overall_status)}`}>
                    {OVERALL_STATUS_LABELS[s.result?.overall_status] || s.result?.overall_status}
                  </span>
                </td>
              ))}
            </Row>
          </tbody>
        </table>
      </TableScroller>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <tr className="border-b border-slate-50">
      <td className="px-3 py-2 text-slate-500 font-medium">{label}</td>
      {children}
    </tr>
  );
}
